/**
 * Reading what the API said when it refused.
 *
 * The API answers every failure with `{ error: { code, message, details? } }`,
 * and that message is written to be shown to a person — the rate-limit copy in
 * particular is the only thing that tells someone why the resend button stopped
 * working. Anything else that arrives is still handled, because a proxy, a
 * gateway or a cold Lambda can put its own body in front of the one the route
 * meant to send.
 *
 * Kept free of anything that touches the network or `import.meta`, so it can be
 * tested as the pure string handling it is.
 */

import { z } from 'zod';

/** The API's own envelope. */
const nestedErrorSchema = z.object({
  error: z.object({
    code: z.string().min(1).optional(),
    message: z.string().min(1).optional(),
    details: z.unknown().optional(),
  }),
});

/** Gateways and older handlers put the same fields at the top level. */
const flatErrorSchema = z.object({
  code: z.string().min(1).optional(),
  message: z.string().min(1).optional(),
  error: z.string().min(1).optional(),
});

export interface DescribedError {
  message: string | null;
  code: string | null;
  details: unknown;
}

export function describeErrorBody(payload: unknown): DescribedError {
  const nested = nestedErrorSchema.safeParse(payload);
  if (nested.success) {
    return {
      message: nested.data.error.message ?? null,
      code: nested.data.error.code ?? null,
      details: nested.data.error.details ?? null,
    };
  }

  const flat = flatErrorSchema.safeParse(payload);
  if (flat.success && (flat.data.message !== undefined || flat.data.error !== undefined)) {
    return {
      message: flat.data.message ?? flat.data.error ?? null,
      code: flat.data.code ?? null,
      details: null,
    };
  }

  if (typeof payload === 'string' && payload.trim() !== '') {
    return { message: payload.trim().slice(0, 300), code: null, details: null };
  }

  return { message: null, code: null, details: null };
}

const STATUS_MESSAGES: Readonly<Record<number, string>> = {
  400: 'The server rejected that request.',
  401: 'Your session has expired. Sign in again.',
  403: 'You do not have access to that.',
  404: 'That no longer exists.',
  409: 'Someone else changed that first. Reload and try again.',
  413: 'That file is larger than the limit.',
  429: 'Too many attempts. Wait a few minutes and try again.',
  500: 'The server hit an error. Try again shortly.',
  501: 'That part of the panel is not finished yet.',
  502: 'The server is not answering. Try again shortly.',
  503: 'The server is not answering. Try again shortly.',
  504: 'The server took too long to answer. Try again shortly.',
};

export function statusMessage(status: number): string {
  return STATUS_MESSAGES[status] ?? `The server answered with ${status}.`;
}

/**
 * `Retry-After` in either spelling the RFC allows: a count of seconds, or an
 * HTTP date. Returns whole seconds from now, or null when the header is absent
 * or unreadable. A date already in the past yields zero rather than a negative
 * countdown.
 */
export function parseRetryAfter(header: string | null, nowMs: number): number | null {
  if (header === null) return null;

  const trimmed = header.trim();
  if (trimmed === '') return null;

  if (/^\d+$/.test(trimmed)) {
    return Number(trimmed);
  }

  const asDate = Date.parse(trimmed);
  if (Number.isNaN(asDate)) return null;
  return Math.max(0, Math.ceil((asDate - nowMs) / 1000));
}
